import { PomodoroService } from "features/widget-workspace/service/PomodoroService";
import React, { useEffect, useState } from "react";
import { GroupPomodoroEventEntity } from "@lifeat/shared/data-models/Rooms/GroupPomodoroEntity";
import { TimerAlarmSoundsMap } from "@lifeat/shared/data-models/widget/Pomodoro/PomodoroWidgetSettingsEntity";
import { GROUP_POMODORO_NODE, GROUP_POMODORO_SETTINGS_NODE, ROOMS_NODE } from "@lifeat/shared/rtdb-nodes/RtdbNodes";
import { useUserContext } from "@contexts/UserContext";
import { db } from "@firebase-local/firebase";
import { DebugScope, logDebug, logError } from "@service/ConsoleLoggerService";
import { updateFocusTime } from "@user/service/AllUserMetricsService";
import { getAlarmSound, getTimerDisplayText } from "../PomodoroUtil";
import { LIFEAT_SPACES_DOC_TITLE, TIMER_DOC_TITLE_SUFFIX } from "../TimerWidgetConstants";
import { useCountdownTimer } from "./useCountdownTimer";
import { usePomodoroSettingsInput } from "./usePomodoroSettingsInput";

function remainingTime(startTime: number, duration: number): number {
    const now = Math.floor(Date.now() / 1000);
    return startTime - now + duration;
}

type Options = {
    roomId: string;
    hidden: boolean;
    alarmSoundOptions: TimerAlarmSoundsMap;
    pomodoroService: PomodoroService;
    audioRef: React.MutableRefObject<HTMLAudioElement | null>;
    onPomodoroCountChange?: (count: number) => void;
    onIsRunningChange?: (isRunning: boolean) => void;
};
const DEFAULT_EVENT: GroupPomodoroEventEntity = {
    type: "pomodoro",
    isRunning: false,
    timestamp: 0,
    duration: 0,
    shortBreakCount: 0,
    pomodoroCount: 0,
};
/**
 * A headless pomodoro timer is a "dumb" version of the timer which
 * listens to a remote timer and synchronizes its state to the remote timer.
 */
export function useServerPomodoro(options: Options) {
    const { user, userData } = useUserContext();

    const [lastEvent, setLastEvent] = useState<GroupPomodoroEventEntity>(DEFAULT_EVENT);
    const { pomodoroSettingsInput, dispatchPomodoroSettingsInput, duration, saveSettings } = usePomodoroSettingsInput(
        options.pomodoroService,
        options.roomId,
    );

    // Subscribe to timer events.
    useEffect(() => {
        const path = `${ROOMS_NODE.name}/${options.roomId}/${GROUP_POMODORO_NODE.name}/${GROUP_POMODORO_NODE.children.LAST_EVENT}`;
        const ref = db.ref(path);
        const listener = ref.on("value", (snapshot) => {
            const value = snapshot.val();
            logDebug(DebugScope.Pomodoro, "last event", value);
            setLastEvent(value ?? DEFAULT_EVENT);
        });

        return () => ref.off("value", listener);
    }, [options.roomId]);

    // Subscribe to timer settings.
    useEffect(() => {
        const path = `${ROOMS_NODE.name}/${options.roomId}/${GROUP_POMODORO_NODE.name}/${GROUP_POMODORO_SETTINGS_NODE.name}`;
        const ref = db.ref(path);
        const listener = ref.on("value", (snapshot) => {
            const value = snapshot.val();
            if (value) {
                dispatchPomodoroSettingsInput({ type: "load", value });
            }
        });

        return () => ref.off("value", listener);
    }, [options.roomId]);

    useEffect(() => {
        options.onIsRunningChange?.(lastEvent.isRunning);
    }, [lastEvent.isRunning]);

    useEffect(() => {
        const eventExpired = Math.floor(Date.now() / 1000) > lastEvent.timestamp + lastEvent.duration;
        countdownTimer.reset(!eventExpired && lastEvent.isRunning);
    }, [lastEvent.isRunning, lastEvent.duration, lastEvent.timestamp]);

    useEffect(() => {
        options.onPomodoroCountChange?.(lastEvent.pomodoroCount);
    }, [lastEvent.pomodoroCount]);

    useEffect(() => {
        dispatchPomodoroSettingsInput({ type: "setTimerType", value: lastEvent.type });
    }, [lastEvent.type]);

    const handleTimerEnd = () => {
        // if user is authenticated and the completed timer is pomodoro, update their focus time metric
        if (user && userData) {
            if (pomodoroSettingsInput.timerType === "pomodoro") {
                updateFocusTime(duration("pomodoro"));
            }
            options.pomodoroService?.logTimerEndEvent?.(pomodoroSettingsInput.timerType);
        }
    };

    const handleAlarmPlayback = () => {
        logDebug(DebugScope.Pomodoro, "Alarm triggered", options.audioRef.current);
        options.audioRef.current?.play().catch((e) => logError("Error playing pomodoro alarm audio: " + e));
    };

    const countdownTimer = useCountdownTimer({
        duration: remainingTime(lastEvent.timestamp, lastEvent.duration) * 1000,
        alarmRingOffset: getAlarmSound(options.alarmSoundOptions, pomodoroSettingsInput.selectedAlarmSound)
            .soundDuration, // Alarm is triggered by the host.
        onTimerEnd: handleTimerEnd,
        onAlarm: handleAlarmPlayback,
    });

    // If the timer isn't running, we can assume the timer is stopped.
    const timerDisplayText =
        lastEvent?.isRunning ?? false
            ? getTimerDisplayText(countdownTimer.state.currMillis)
            : getTimerDisplayText(duration("pomodoro") * 60 * 1000);

    useEffect(() => {
        if (!options.hidden) {
            if (lastEvent.isRunning) {
                document.title = `${timerDisplayText}${TIMER_DOC_TITLE_SUFFIX}`;
            } else {
                document.title = LIFEAT_SPACES_DOC_TITLE;
            }
        }
    }, [lastEvent.isRunning, timerDisplayText, options.hidden]);

    return {
        lastEvent,
        timerDisplayText,
        pomodoroSettingsInput,
        dispatchPomodoroSettingsInput,
        duration,
        saveSettings,
    };
}
